import React, { useEffect, useState, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBitcoinSign } from '@fortawesome/free-solid-svg-icons';
import './FallingBitcoins.css';

const FallingBitcoins = ({ isActive, duration = 4500 }) => {
    const [bitcoins, setBitcoins] = useState([]);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const intervalRef = useRef(null);
    const bitcoinIdRef = useRef(0);
    const fadeTimeoutRef = useRef(null);
    const clearTimeoutRef = useRef(null);

    useEffect(() => {
        if (!isActive) {
            // Clean up everything when becoming inactive
            setIsFadingOut(false);
            setBitcoins([]);
            
            // Clear all intervals and timeouts
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            if (fadeTimeoutRef.current) {
                clearTimeout(fadeTimeoutRef.current);
                fadeTimeoutRef.current = null;
            }
            if (clearTimeoutRef.current) {
                clearTimeout(clearTimeoutRef.current);
                clearTimeoutRef.current = null;
            }
            return;
        }

        // Reset state when becoming active
        setIsFadingOut(false);
        bitcoinIdRef.current = 0; // Reset bitcoin ID counter

        // Function to generate a single bitcoin
        const generateBitcoin = () => {
            const id = bitcoinIdRef.current++;
            return {
                id: `bitcoin-${Date.now()}-${id}`,
                left: Math.random() * 100, // Random horizontal position (0-100%)
                animationDuration: 2.5 + Math.random() * 1.5, // Random duration (2.5-4s) - balanced speed
                size: 16 + Math.random() * 32, // Random size (16-48px)
                opacity: 0.4 + Math.random() * 0.4, // Random opacity (0.4-0.8)
            };
        };

        // Generate initial batch of bitcoins
        const initialBitcoins = [];
        for (let i = 0; i < 15; i++) {
            const bitcoin = generateBitcoin();
            bitcoin.animationDelay = Math.random() * 2; // Spread initial bitcoins
            initialBitcoins.push(bitcoin);
        }
        setBitcoins(initialBitcoins);

        // Continuously spawn new bitcoins
        intervalRef.current = setInterval(() => {
            setBitcoins(prev => {
                // Remove old bitcoins that have finished animating
                const now = Date.now();
                const filtered = prev.filter(bitcoin => {
                    const bitcoinAge = now - parseInt(bitcoin.id.split('-')[1]);
                    return bitcoinAge < (bitcoin.animationDuration + (bitcoin.animationDelay || 0)) * 1000;
                });

                // Add 1-3 new bitcoins
                const numNewBitcoins = 1 + Math.floor(Math.random() * 3);
                const newBitcoins = [];
                for (let i = 0; i < numNewBitcoins; i++) {
                    newBitcoins.push(generateBitcoin());
                }

                return [...filtered, ...newBitcoins];
            });
        }, 300); // Spawn new bitcoins every 300ms - slightly slower spawn rate

        // Start fade out before duration ends
        fadeTimeoutRef.current = setTimeout(() => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            setIsFadingOut(true);
            
            // Clear bitcoins after fade out completes
            clearTimeoutRef.current = setTimeout(() => {
                setBitcoins([]);
                setIsFadingOut(false);
            }, 1000); // 1 second fade out
        }, duration - 1000); // Start fade 1 second before end

        // Cleanup function
        return () => {
            // Clear all intervals and timeouts
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            if (fadeTimeoutRef.current) {
                clearTimeout(fadeTimeoutRef.current);
                fadeTimeoutRef.current = null;
            }
            if (clearTimeoutRef.current) {
                clearTimeout(clearTimeoutRef.current);
                clearTimeoutRef.current = null;
            }
            // Reset state
            setBitcoins([]);
            setIsFadingOut(false);
        };
    }, [isActive, duration]);

    if (!isActive || bitcoins.length === 0) return null;

    return (
        <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            pointerEvents="none"
            zIndex={999999}
            overflow="hidden"
            style={{
                opacity: isFadingOut ? 0 : 1,
                transition: 'opacity 1s ease-out'
            }}
        >
            {bitcoins.map((bitcoin) => (
                <Box
                    key={bitcoin.id}
                    className="falling-bitcoin"
                    position="absolute"
                    top="-50px"
                    left={`${bitcoin.left}%`}
                    style={{
                        animationDelay: `${bitcoin.animationDelay || 0}s`,
                        animationDuration: `${bitcoin.animationDuration}s`,
                    }}
                >
                    <FontAwesomeIcon
                        icon={faBitcoinSign}
                        size="2x"
                        color="#f7931a"
                        style={{ 
                            fontSize: `${bitcoin.size}px`,
                            opacity: bitcoin.opacity
                        }}
                    />
                </Box>
            ))}
        </Box>
    );
};

export default FallingBitcoins;